import type { ReactNode } from 'react';
import {
  CaretDoubleRight,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import Tooltip from '@/components/ui/tooltip';

export interface DocumentViewerControlsProps {
  currentPage: number | null;
  numPages: number;
  canZoomOut: boolean;
  canZoomIn: boolean;
  onZoomOut: () => void;
  onZoomIn: () => void;
  /**
   * Collapse the document panel (the host restores its chat-only layout).
   * The control reads as "collapse"; leaving the full view is the host's.
   */
  onClose?: () => void;
  /** The host's own compact controls (a lecture's Study menu). */
  actions?: ReactNode;
}

interface DocumentViewerToolbarProps extends DocumentViewerControlsProps {
  /**
   * Null where the host already shows the name; the controls then sit right.
   * Undefined while the name is not known yet: a placeholder holds its place.
   */
  fileName: string | null | undefined;
}

const ToolbarButton = ({
  label,
  children,
  ...props
}: React.ComponentProps<typeof Button> & { label: string }) => (
  <Tooltip title={label}>
    <Button size="icon-sm" variant="ghost" aria-label={label} {...props}>
      {children}
    </Button>
  </Tooltip>
);

/**
 * The page indicator and zoom controls, for the viewer's own toolbar or a
 * host header that renders them in place of it (`renderToolbar`).
 */
export const DocumentViewerControls = ({
  currentPage,
  numPages,
  canZoomOut,
  canZoomIn,
  onZoomOut,
  onZoomIn,
  onClose,
  actions,
}: DocumentViewerControlsProps) => {
  // Zoom only means something once there are pages to zoom.
  const hasPages = numPages > 0;

  return (
    <>
      {hasPages ? (
        <span
          className="shrink-0 px-1 text-xs tabular-nums text-fg-secondary"
          aria-live="polite"
          data-testid="document-viewer-page-indicator"
        >
          {`p. ${currentPage ?? 1} / ${numPages}`}
        </span>
      ) : (
        // Holds the indicator's width until the page count is known, so the
        // controls do not jump; nothing is shown or announced meanwhile.
        <span
          className="invisible shrink-0 px-1 text-xs tabular-nums"
          aria-hidden="true"
        >
          p. 1 / 10
        </span>
      )}
      {actions}
      <ToolbarButton
        label="Zoom out"
        onClick={onZoomOut}
        disabled={!hasPages || !canZoomOut}
      >
        <MagnifyingGlassMinus size={16} aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        label="Zoom in"
        onClick={onZoomIn}
        disabled={!hasPages || !canZoomIn}
      >
        <MagnifyingGlassPlus size={16} aria-hidden="true" />
      </ToolbarButton>
      {onClose ? (
        <ToolbarButton label="Collapse document" onClick={onClose}>
          <CaretDoubleRight size={16} aria-hidden="true" />
        </ToolbarButton>
      ) : null}
    </>
  );
};

const DocumentViewerToolbar = ({
  fileName,
  ...controls
}: DocumentViewerToolbarProps) => (
  <div
    data-document-viewer-toolbar
    className="flex min-h-11 shrink-0 items-center gap-1 border-b border-border-subtle bg-surface-canvas px-2 py-1"
  >
    {fileName === undefined ? (
      <span className="flex min-w-0 flex-1 px-1">
        <Skeleton
          variant="text"
          width="10rem"
          height="1rem"
          className="max-w-full"
          data-testid="document-viewer-name-placeholder"
        />
      </span>
    ) : fileName !== null ? (
      <span
        className="min-w-0 flex-1 truncate px-1 text-sm font-medium text-fg-primary"
        title={fileName}
      >
        {fileName}
      </span>
    ) : (
      <span className="flex-1" aria-hidden="true" />
    )}
    <DocumentViewerControls {...controls} />
  </div>
);

export default DocumentViewerToolbar;
